import clsx from 'clsx';
import Spinner from './Spinner';

interface LoadingOverlayProps {
  /** Whether the overlay is visible */
  isLoading: boolean;
  /** Children to render behind the overlay */
  children: React.ReactNode;
  /** Additional class name for the container */
  className?: string;
  /** Opacity when loading (0-100) */
  loadingOpacity?: number;
  /** Show spinner in center */
  showSpinner?: boolean;
  /** Custom loading text */
  loadingText?: string;
  /** Whether the content is in a stale state (has old data while loading new) */
  isStale?: boolean;
}

/**
 * Loading overlay component that fades content while data is loading.
 * Provides smooth transitions and optional spinner.
 */
export default function LoadingOverlay({
  isLoading,
  children,
  className,
  loadingOpacity = 40,
  showSpinner = true,
  loadingText,
  isStale = false,
}: LoadingOverlayProps) {
  return (
    <div className={clsx('relative', className)}>
      {/* Content with opacity transition */}
      <div
        className={clsx(
          'transition-all duration-300 ease-in-out',
          isLoading && 'pointer-events-none'
        )}
        style={{
          opacity: isLoading ? loadingOpacity / 100 : 1,
          filter: isLoading ? 'blur(0.5px)' : 'none',
        }}
      >
        {children}
      </div>

      {/* Loading spinner overlay */}
      {isLoading && showSpinner && (
        <div className="absolute inset-0 flex items-center justify-center z-10">
          <div className="flex flex-col items-center gap-2">
            <Spinner size="lg" className="text-primary-500" />
            {loadingText && (
              <span className="text-sm text-slate-600 dark:text-slate-300 font-medium">{loadingText}</span>
            )}
            {isStale && (
              <span className="text-xs text-slate-400 dark:text-slate-500">Updating...</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Simple skeleton loader for text/data
 */
export function Skeleton({
  className,
  width,
  height = '1rem',
}: {
  className?: string;
  width?: string;
  height?: string;
}) {
  return (
    <div
      className={clsx(
        'animate-pulse bg-slate-200 dark:bg-slate-700 rounded',
        className
      )}
      style={{ width, height }}
    />
  );
}

/**
 * Component for displaying data that might not be connected to backend.
 * Shows red text and reduced opacity when not connected.
 */
interface DataValueProps {
  /** The value to display */
  value: React.ReactNode;
  /** Whether this value is connected to real backend data */
  isConnected?: boolean;
  /** Loading state */
  isLoading?: boolean;
  /** Additional class name */
  className?: string;
  /** Placeholder shown when not connected */
  placeholder?: string;
}

export function DataValue({
  value,
  isConnected = true,
  isLoading = false,
  className,
  placeholder = '--',
}: DataValueProps) {
  if (isLoading) {
    return <Skeleton className={className} width="4rem" />;
  }

  if (!isConnected) {
    return (
      <span
        className={clsx('text-red-500 opacity-50', className)}
        title="Not connected to backend"
      >
        {placeholder}
      </span>
    );
  }

  return <span className={className}>{value}</span>;
}

/**
 * Widget wrapper that shows reduced opacity and indicator when not connected
 */
interface WidgetWrapperProps {
  /** Whether this widget is connected to real backend data */
  isConnected?: boolean;
  /** Whether data is loading */
  isLoading?: boolean;
  /** Children to render */
  children: React.ReactNode;
  /** Additional class name */
  className?: string;
  /** Show connection indicator badge */
  showIndicator?: boolean;
}

export function WidgetWrapper({
  isConnected = true,
  isLoading = false,
  children,
  className,
  showIndicator = true,
}: WidgetWrapperProps) {
  return (
    <div
      className={clsx(
        'relative transition-opacity duration-300',
        !isConnected && !isLoading && 'opacity-20',
        isLoading && 'opacity-40',
        className
      )}
    >
      {children}
      {showIndicator && !isConnected && !isLoading && (
        <div className="absolute top-2 right-2">
          <div
            className="w-2 h-2 rounded-full bg-red-500"
            title="Not connected to backend"
          />
        </div>
      )}
    </div>
  );
}
